import _ from 'lodash';
import type { TypePolicies } from '@apollo/client';
import type { ConversationMessagesHQ_conversationMessages_messages as ConversationMessageHQ } from '../__generated__/ConversationMessagesHQ';
import type { ConversationsListHQ_conversationsList_items as ConversationHQ } from '../__generated__/ConversationsListHQ';
import { keyBy, uniqBy } from 'lib/apollo/cache/cache_helpers';

// typescript's satisfies would be awesome here

/**
 * Express the apollo cache handling for HQ conversations, notice that is a departure
 * from r101-apollo way of doing cache managing, here are a few differences that
 * also would want to use for the r101-apollo schema going forward to avoid
 * common apollo/client problems and pitfalls:
 *
 * - don't normalize at all
 * - use the cache as simple global store of query results
 * - if you need to do optmistic updates then update the cache directly via updateQuery or writeQuery (fully typed)
 * - don't let apollo interfer with merge policies in weird cases that are undocumented by using overwrite: true
 *
 *
 * Notice that since we are not normalizing, merge functions receive actual objects instead of apollo cache
 * ref's that make it harder to do sorting and merging, in this way this becomes a really dumb cache reducer
 */
const hqConvoTypePolicies: TypePolicies = {
  ConversationMessagesView: {
    fields: {
      // Necessary for pagination, see Query.fields.conversationMessages
      messages: {
        // We get actual objects here because we are not normalizing data inside it,
        // instead we are storing the query response as is (this is preferred going forward),
        // so it is much easier to inspect objects and do the merging as needed
        merge: (
          existing: Array<Partial<ConversationMessageHQ>> = [],
          incoming: Array<Partial<ConversationMessageHQ>> | undefined
        ) => {
          // merge incoming into existing with the same uuid

          // TODO: I removed the type safety here since this became a nested
          // attribute I couldn't find a way around the type safety issue so I
          // ignored it for now to unblock frontend.
          const existingByUuid = _.keyBy(existing, 'message.compositeKey');
          const incomingByUuid = _.keyBy(incoming ?? [], 'message.compositeKey');

          // notice that incoming comes first, so that we can override existing
          // make sure there's only one item pero uuid
          const merged = _.merge({}, existingByUuid, incomingByUuid);
          const items = Object.values(merged);

          // unify by uuid with merging
          const itemsUniq = _.uniqBy(items, 'message.compositeKey');

          // we want to sort by descending sequenceId
          const itemsSorted = _.sortBy(itemsUniq, 'sequenceId').slice().reverse();
          return itemsSorted;
        },
      },
    },
  },

  ConversationsListView: {
    fields: {
      // Necessary for pagination, see Query.fields.conversationsList
      items: {
        merge: (
          existing: Array<Partial<ConversationHQ>> = [],
          incoming: Array<Partial<ConversationHQ>> | undefined
        ) => {
          // merge incoming into existing with the same uuid
          const existingByUuid = keyBy(existing, 'conversationKey.conversationUuid');
          const incomingByUuid = keyBy(incoming ?? [], 'conversationKey.conversationUuid');

          // notice that incoming comes first, so that we can override existing
          // make sure there's only one item pero uuid
          const merged = _.merge({}, existingByUuid, incomingByUuid);
          const items = Object.values(merged);

          // unify by uuid with merging
          const itemsUniq = uniqBy(items, 'conversationKey.conversationUuid');

          // we want to sort by descending lastMessageTime
          const itemsSorted = _.sortBy(itemsUniq, 'lastMessageTime').slice().reverse();
          // TODO we might need to sort by unreads too
          return itemsSorted;
        },
      },
    },
  },

  Query: {
    fields: {
      conversationMessages: {
        // we only care about `input.conversation` to store the cache
        keyArgs: ['input', ['conversationKey']],
      },

      conversationsList: {
        // we only care about `input.organization` to store the cache
        keyArgs: ['input', ['organization']],
      },
    },
  },
};

export default hqConvoTypePolicies;
